import { TreePine, Download, X, AlertTriangle } from "lucide-react";

import { Button } from "@/components/ui/button";
import {
  Table,
  TableBody,
  TableCell,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { SortableTableHead } from "@/components/ui/sortable-table-head";
import {
  Empty,
  EmptyHeader,
  EmptyMedia,
  EmptyTitle,
  EmptyDescription,
} from "@/components/ui/empty";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { cn } from "@/lib/utils";

import { useTableSort } from "../../../../sales-tools/opportunities/hooks/use-table-sort";
import type { ParcelInfo } from "../types/deforestation";
import { TransitionDonut } from "./transition-donut";
import { ForestCoverTrend } from "./forest-cover-trend";

interface GroupPanelProps {
  parcels: ParcelInfo[];
  groupName?: string;
  onExport: () => void;
  onClose: () => void;
  onFocusParcel: (cadastrePid: string) => void;
}

type SortColumn =
  | "parcel_id"
  | "area_ha"
  | "forest_change_pct"
  | "density_change"
  | "regulated";

// Highest-stakes regulated category present on the parcel.
// A/B = protected (worst), C/R = lower tiers, X = clearable.
function regulatedRank(parcel: ParcelInfo): number {
  const cats = (parcel.stats?.regulatedVeg ?? []).map((r) =>
    r.category.charAt(0).toUpperCase(),
  );
  if (cats.includes("A")) return 4;
  if (cats.includes("B")) return 3;
  if (cats.includes("C")) return 2;
  if (cats.includes("R")) return 1;
  return 0;
}

function regulatedLabel(parcel: ParcelInfo): string {
  const rank = regulatedRank(parcel);
  return ["None", "R", "C", "B", "A"][rank];
}

function densityChange(parcel: ParcelInfo): number {
  const s = parcel.stats;
  if (!s || s.treeDensity2020 === undefined || s.treeDensity2025 === undefined) {
    return 0;
  }
  return s.treeDensity2025 - s.treeDensity2020;
}

export function GroupPanel({
  parcels,
  groupName,
  onExport,
  onClose,
  onFocusParcel,
}: GroupPanelProps) {
  const { sortColumn, sortDirection, handleSort, sortedData } = useTableSort<
    ParcelInfo,
    SortColumn
  >({
    data: parcels,
    defaultColumn: "forest_change_pct",
    defaultDirection: "asc",
    accessors: {
      parcel_id: (p) => p.parcel_id,
      area_ha: (p) => p.area_ha,
      forest_change_pct: (p) => p.stats?.forestChange?.net_pct ?? 0,
      density_change: (p) => densityChange(p),
      regulated: (p) => regulatedRank(p),
    },
  });

  if (parcels.length === 0) {
    return (
      <div className="p-4">
        <Empty>
          <EmptyHeader>
            <EmptyMedia variant="icon">
              <TreePine />
            </EmptyMedia>
            <EmptyTitle>No parcels selected</EmptyTitle>
            <EmptyDescription>
              Click parcels on the map or search to add them to this group.
            </EmptyDescription>
          </EmptyHeader>
        </Empty>
      </div>
    );
  }

  // ─── Aggregates ──────────────────────────────────────────────────
  // Sum tree areas in m² (only parcels with sidecar stats contribute);
  // total_cadastre_area uses areaM2 from the sidecar so divisions by it
  // align with the SatSure-clipped numerator. Falls back to area_ha if a
  // parcel has no sidecar entry yet (e.g. just-fetched cadastre, sidecar
  // not loaded).
  const totalCadastreM2 = parcels.reduce(
    (s, p) => s + (p.stats?.areaM2 ?? p.area_ha * 10_000),
    0,
  );
  const totalTreeArea2020M2 = parcels.reduce(
    (s, p) => s + (p.stats?.treeArea2020M2 ?? 0),
    0,
  );
  const totalTreeArea2025M2 = parcels.reduce(
    (s, p) => s + (p.stats?.treeArea2025M2 ?? 0),
    0,
  );

  const treeCover2020Ha = totalTreeArea2020M2 / 10_000;
  const treeCover2025Ha = totalTreeArea2025M2 / 10_000;
  const treeCover2020Pct =
    totalCadastreM2 > 0 ? (totalTreeArea2020M2 / totalCadastreM2) * 100 : 0;
  const treeCover2025Pct =
    totalCadastreM2 > 0 ? (totalTreeArea2025M2 / totalCadastreM2) * 100 : 0;

  const netForestChangeHa = (totalTreeArea2025M2 - totalTreeArea2020M2) / 10_000;

  let weightedDensityChange = 0;
  let weightTotal = 0;
  for (const p of parcels) {
    const s = p.stats;
    if (!s?.treeArea2020M2 || s.treeDensity2020 === undefined ||
        s.treeDensity2025 === undefined) continue;
    const w = s.treeArea2020M2;
    weightedDensityChange += (s.treeDensity2025 - s.treeDensity2020) * w;
    weightTotal += w;
  }
  const avgDensityChange =
    weightTotal > 0 ? weightedDensityChange / weightTotal : 0;

  // ─── Regulatory summary: parcels carrying any A or B coverage ─────
  // (A/B = the protected categories that gate clearing decisions). Sums
  // the parcel cadastre area when at least one A/B row is present —
  // good enough for the group call-out; per-pixel A/B intersect with
  // forest loss is a future improvement.
  let regulatedParcelCount = 0;
  let regulatedAreaHa = 0;
  for (const p of parcels) {
    const veg = p.stats?.regulatedVeg ?? [];
    let hasProtected = false;
    let pct = 0;
    for (const r of veg) {
      const cat = r.category.charAt(0).toUpperCase();
      if (cat === "A" || cat === "B") {
        hasProtected = true;
        pct += r.coverage_pct;
      }
    }
    if (hasProtected) {
      regulatedParcelCount += 1;
      regulatedAreaHa += ((p.stats?.areaM2 ?? p.area_ha * 10_000) * pct) / 100 / 10_000;
    }
  }

  return (
    <div className="flex flex-col gap-4 p-4">
      {/* Header */}
      <div className="space-y-1">
        <div className="flex items-center gap-1">
          <h2 className="text-lg w-full font-semibold truncate">
            {groupName ?? "Group Summary"}
          </h2>
          <Button variant="outline" size="sm" onClick={onExport}>
            <Download className="mr-1 h-4 w-4" />
            Export
          </Button>
          <Button variant="ghost" size="icon" onClick={onClose}>
            <X className="h-4 w-4" />
          </Button>
        </div>
        <p className="text-sm text-muted-foreground">
          {parcels.length} Parcel{parcels.length !== 1 ? "s" : ""}
        </p>
      </div>

      {/* Summary stats — 2x2 */}
      <div className="grid grid-cols-2 gap-3">
        <StatTile
          label="Tree Cover 2020"
          value={`${treeCover2020Ha.toFixed(1)} Ha`}
          sublabel={`${treeCover2020Pct.toFixed(1)}% of total area`}
        />
        <StatTile
          label="Tree Cover 2025"
          value={`${treeCover2025Ha.toFixed(1)} Ha`}
          sublabel={`${treeCover2025Pct.toFixed(1)}% of total area`}
        />
        <StatTile
          label="Net Forest Change"
          value={`${netForestChangeHa > 0 ? "+" : ""}${netForestChangeHa.toFixed(1)} Ha`}
          sublabel="2020–2025"
          valueClassName={cn(
            netForestChangeHa < 0 ? "text-red-500" : "text-green-500",
          )}
        />
        <StatTile
          label="Avg Density Change"
          value={`${avgDensityChange > 0 ? "+" : ""}${avgDensityChange.toFixed(1)}`}
          sublabel="Weighted average"
          valueClassName={cn(
            avgDensityChange < 0 ? "text-red-500" : "text-green-500",
          )}
        />
      </div>

      {/* Transitions donut */}
      <TransitionDonut parcels={parcels} />

      {/* Regulatory summary */}
      <div>
        <h3 className="mb-2 text-sm font-medium">Regulatory Summary</h3>
        {regulatedParcelCount > 0 ? (
          <Alert variant="destructive">
            <AlertTriangle className="h-4 w-4" />
            <AlertDescription>
              {regulatedParcelCount} parcel
              {regulatedParcelCount !== 1 ? "s contain" : " contains"} regulated
              vegetation (A/B; {regulatedAreaHa.toFixed(1)} Ha)
            </AlertDescription>
          </Alert>
        ) : (
          <p className="text-sm text-muted-foreground">
            No regulated vegetation (A/B) detected.
          </p>
        )}
      </div>

      {/* Forest cover bars */}
      <ForestCoverTrend parcels={parcels} />

      {/* Parcel table */}
      <div>
        <h3 className="mb-2 text-sm font-medium">Parcels</h3>
        <div className="rounded-md border">
          <Table>
            <TableHeader>
              <TableRow>
                <SortableTableHead
                  column="parcel_id"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort as (column: string) => void}
                >
                  Parcel ID
                </SortableTableHead>
                <SortableTableHead
                  column="area_ha"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort as (column: string) => void}
                  align="right"
                >
                  Area (Ha)
                </SortableTableHead>
                <SortableTableHead
                  column="forest_change_pct"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort as (column: string) => void}
                  align="right"
                >
                  Change (%)
                </SortableTableHead>
                <SortableTableHead
                  column="density_change"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort as (column: string) => void}
                  align="right"
                >
                  Density Chg
                </SortableTableHead>
                <SortableTableHead
                  column="regulated"
                  sortColumn={sortColumn}
                  sortDirection={sortDirection}
                  onSort={handleSort as (column: string) => void}
                >
                  Regulated
                </SortableTableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {sortedData.map((parcel) => {
                const change = parcel.stats?.forestChange?.net_pct ?? 0;
                const dens = densityChange(parcel);
                return (
                  <TableRow
                    key={parcel.cadastrePid}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => onFocusParcel(parcel.cadastrePid)}
                  >
                    <TableCell className="font-mono text-xs">
                      {parcel.parcel_id}
                    </TableCell>
                    <TableCell className="text-right">
                      {parcel.area_ha > 0 ? parcel.area_ha.toFixed(1) : "—"}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right",
                        change < 0
                          ? "text-red-500"
                          : change > 0
                            ? "text-green-500"
                            : "text-muted-foreground",
                      )}
                    >
                      {parcel.stats?.forestChange
                        ? `${change > 0 ? "+" : ""}${change.toFixed(1)}%`
                        : "—"}
                    </TableCell>
                    <TableCell
                      className={cn(
                        "text-right",
                        dens < 0
                          ? "text-red-500"
                          : dens > 0
                            ? "text-green-500"
                            : "text-muted-foreground",
                      )}
                    >
                      {parcel.stats?.treeDensity2020 !== undefined
                        ? `${dens > 0 ? "+" : ""}${dens.toFixed(1)}`
                        : "—"}
                    </TableCell>
                    <TableCell>{regulatedLabel(parcel)}</TableCell>
                  </TableRow>
                );
              })}
            </TableBody>
          </Table>
        </div>
      </div>
    </div>
  );
}

interface StatTileProps {
  label: string;
  value: string;
  sublabel: string;
  valueClassName?: string;
}

function StatTile({ label, value, sublabel, valueClassName }: StatTileProps) {
  return (
    <div className="rounded-lg bg-muted p-3">
      <p className="text-xs text-muted-foreground">{label}</p>
      <p className={cn("text-lg font-semibold", valueClassName)}>{value}</p>
      <p className="text-xs text-muted-foreground">{sublabel}</p>
    </div>
  );
}
